import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { refreshNumberingModes } from "@finance/components/DocNumberField";
import { toast } from "sonner";
import { Hash, FloppyDisk, Gear, Warning } from "@phosphor-icons/react";

const DATE_LABELS = { none: "None", year: "Year (YYYY)", year_month: "Year-Month (YYYYMM)", full_date: "Full date (YYYYMMDD)" };

function livePreview(cfg) {
  const digits = Math.max(1, Number(cfg.digits) || 5);
  const seq = Math.max(Number(cfg.next) || 1, Number(cfg.start) || 1);
  const d = new Date();
  const y = String(d.getFullYear());
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const seg = cfg.date_segment === "year" ? y : cfg.date_segment === "year_month" ? y + m : cfg.date_segment === "full_date" ? y + m + day : "";
  return `${cfg.prefix || ""}${seg}${String(seq).padStart(digits, "0")}${cfg.suffix || ""}`;
}

function RuleCard({ row, onSaved }) {
  const [cfg, setCfg] = useState(row);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCfg(row); }, [row]);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    const currentSeq = Math.max(Number(row.next) || 1, Number(row.start) || 1);
    if (cfg.mode !== "manual" && Number(cfg.start) < currentSeq) {
      if (!window.confirm(`This lowers the "${cfg.label}" sequence from ${currentSeq} to ${cfg.start}. New documents may reuse numbers already issued and collide with existing records. Continue?`)) return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/settings/document-numbering/${cfg.key}`, {
        mode: cfg.mode || "auto",
        prefix: cfg.prefix || "", suffix: cfg.suffix || "",
        date_segment: cfg.date_segment || "none",
        digits: Number(cfg.digits) || 5, start: Number(cfg.start) || 0,
      });
      toast.success(cfg.mode === "manual" ? `${cfg.label} — set to Manual (you type the number)` : cfg.mode === "off" ? `${cfg.label} — numbering turned OFF (issued without a number)` : `${cfg.label} — next number will be ${r.data.preview}`);
      refreshNumberingModes();
      onSaved?.(r.data);
    } catch (e) { toast.error(errMsg(e) || "Could not save"); }
    finally { setSaving(false); }
  };

  const manual = cfg.mode === "manual";
  const off = cfg.mode === "off";
  const locked = manual || off;
  const atRisk = !!cfg.system_generated && (cfg.mode || "auto") !== "auto";

  return (
    <div className="rounded-sm border border-gray-200 bg-white p-5" data-testid={`numbering-card-${cfg.key}`}>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="font-display text-base font-semibold text-gray-900">{cfg.label}</div>
          <div className="text-xs text-gray-400">{off ? "Off · issued without a number" : manual ? "Manual · you type the number (checked for duplicates)" : "Automated · applies to new documents only"}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex overflow-hidden rounded-sm border border-gray-200" data-testid={`numbering-mode-${cfg.key}`}>
            {[["auto", "Automated"], ["manual", "Manual"], ["off", "Off"]].map(([m, lbl]) => (
              <button key={m} type="button" data-testid={`numbering-mode-${m}-${cfg.key}`}
                className={`px-3 py-1 text-xs font-semibold transition-colors ${cfg.mode === m ? "bg-[#1B4383] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                onClick={() => set("mode", m)}>{lbl}</button>
            ))}
          </div>
          {!locked && (
            <div className="rounded-sm bg-gray-900 px-2.5 py-1 font-mono text-xs text-white" data-testid={`numbering-preview-${cfg.key}`}>
              Next: {livePreview(cfg)}
            </div>
          )}
        </div>
      </div>
      {cfg.system_generated && (
        <div className={`mb-3 flex items-start gap-2 rounded-sm px-3 py-2 text-xs ${atRisk ? "border border-amber-200 bg-amber-50 text-amber-800" : "bg-blue-50/60 text-blue-700"}`} data-testid={`numbering-systemflag-${cfg.key}`}>
          <Gear size={14} className="mt-0.5 shrink-0" />
          <span>
            <b>System-generated.</b> {cfg.system_note}
            {atRisk && " This type is set to " + (manual ? "Manual" : "Off") + " — the automatic flow will still auto-number it, but any hand-created document of this type must be numbered manually."}
          </span>
        </div>
      )}
      <div className={`grid grid-cols-2 gap-3 md:grid-cols-5 ${locked ? "pointer-events-none opacity-40" : ""}`}>
        <div>
          <Label className="lx-label">Prefix</Label>
          <Input className="mt-1" data-testid={`numbering-prefix-${cfg.key}`} value={cfg.prefix || ""} onChange={(e) => set("prefix", e.target.value)} placeholder="e.g. INV-" />
        </div>
        <div>
          <Label className="lx-label">Date segment</Label>
          <Select value={cfg.date_segment || "none"} onValueChange={(v) => set("date_segment", v)}>
            <SelectTrigger className="mt-1" data-testid={`numbering-date-${cfg.key}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.keys(DATE_LABELS).map((k) => <SelectItem key={k} value={k}>{DATE_LABELS[k]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="lx-label">Digits (leading zeros)</Label>
          <Input type="number" min={1} max={12} className="mt-1" data-testid={`numbering-digits-${cfg.key}`} value={cfg.digits ?? 5} onChange={(e) => set("digits", e.target.value)} />
        </div>
        <div>
          <Label className="lx-label">Starting number</Label>
          <Input type="number" min={0} className="mt-1" data-testid={`numbering-start-${cfg.key}`} value={cfg.start ?? 1} onChange={(e) => set("start", e.target.value)} />
        </div>
        <div>
          <Label className="lx-label">Suffix (optional)</Label>
          <Input className="mt-1" data-testid={`numbering-suffix-${cfg.key}`} value={cfg.suffix || ""} onChange={(e) => set("suffix", e.target.value)} placeholder="e.g. /LFM" />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs text-gray-400">Sequence currently at <span className="font-mono text-gray-600">{Math.max(Number(cfg.next) || 1, Number(cfg.start) || 1)}</span> — changing the starting number resets the sequence going forward.</div>
        <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid={`numbering-save-${cfg.key}`} disabled={saving} onClick={save}>
          <FloppyDisk size={16} className="mr-1" />{saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

export default function DocumentNumbering() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/settings/document-numbering");
      setRows(r.data.rows || []);
    } catch (e) {
      setError(errMsg(e) || "You do not have access to these settings.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSaved = (updated) => setRows((rs) => rs.map((r) => (r.key === updated.key ? { ...r, ...updated } : r)));

  return (
    <Layout>
      <PageHeader
        title="Document Numbering"
        subtitle="Configure the automated numbering rule for every transactional document. Rules apply to new documents only."
      />
      <div className="p-8">
        {error ? (
          <div className="rounded-sm border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400" data-testid="numbering-no-access">{error}</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-sm border border-blue-100 bg-blue-50/50 px-4 py-3 text-sm text-blue-800">
              <Hash size={18} /> Set the prefix, optional date segment, number of digits and the starting number for each document type. A live preview shows the next number that will be issued.
            </div>
            {rows.some((r) => r.at_risk) && (
              <div className="flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="numbering-audit-warning">
                <Warning size={18} className="mt-0.5 shrink-0" />
                <span>Some <b>system-generated</b> document types are set to Manual or Off. Those automatic flows (e.g. stock fulfillment, expense payments) will keep auto-numbering so nothing breaks — but review the highlighted cards below so hand-created documents of the same type are numbered as you expect.</span>
              </div>
            )}
            {rows.map((r) => <RuleCard key={r.key} row={r} onSaved={onSaved} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
